/*!
 * Xtenia Score, advanced calisthenics assessment
 * Copyright (c) 2026 Xtenia. All rights reserved.
 * https://www.xtenia.com/
 *
 * Proprietary software. Unauthorized copying, distribution,
 * or use of this code is prohibited without written permission.
 */

/* ============================================================
   Xtenia SCORE, component library
   Phases 1-4 + Polish Pass.

   Polish additions:
     - Info icon redesigned as minimal "i" mark, no border button
     - Tooltips can include an optional link via infoLinkKey + infoLinkUrlKey
     - Contextual links rendered after specific tier/chip selections
     - Step-level link list (links_block component)
============================================================ */

(function() {
  'use strict';

  window.WD = window.WD || {};
  window.WD.score = window.WD.score || {};

  var WS = window.WD.score;
  WS.components = {};

  function t(key) {
    return (WS.t && key) ? WS.t(key) : (key || '');
  }

  function el(tag, className, attrs) {
    var n = document.createElement(tag);
    if (className) n.className = className;
    if (attrs) {
      for (var k in attrs) {
        if (k === 'text') n.textContent = attrs[k];
        else if (k === 'html') n.innerHTML = attrs[k];
        else n.setAttribute(k, attrs[k]);
      }
    }
    return n;
  }

  /**
   * NUCLEAR THEME OVERRIDE
   * Some Shopify themes set Primary button background via :where() or
   * other low-specificity selectors. Even with !important + attribute
   * selectors, edge cases slip through. Setting inline styles on each
   * button at creation/state-change time guarantees our look wins.
   *
   * Usage: forceButtonStyle(button, state)
   *   state = 'idle' | 'active' | 'yes-active' | 'no-active'
   */
  /**
   * Atomically write all critical button style properties.
   * Uses cssText so all properties land in one operation,
   * making it harder for a theme MutationObserver to win.
   * Stores intent on a data attribute so we can re-apply
   * if styles get stripped.
   */
  function forceButtonStyle(btn, state) {
    if (!btn || !btn.style) return;

    // Tag the desired state for later re-application
    btn.setAttribute('data-ws-state', state || 'idle');

    var bg, border, color, extra = '';
    if (state === 'active') {
      bg = 'rgba(227, 61, 32, 0.18)';
      border = '1.5px solid #e33d20';
      color = '#fff';
      extra = 'box-shadow: 0 0 0 3px rgba(227, 61, 32, 0.10), 0 4px 14px rgba(227, 61, 32, 0.25) !important;';
    } else if (state === 'yes-active') {
      bg = 'rgba(34, 197, 94, 0.15)';
      border = '1.5px solid #22c55e';
      color = '#fff';
    } else if (state === 'no-active') {
      bg = 'rgba(239, 68, 68, 0.15)';
      border = '1.5px solid #ef4444';
      color = '#fff';
    } else if (state === 'nav-primary') {
      bg = 'rgba(227, 61, 32, 0.10)';
      border = '1.5px solid #e33d20';
      color = '#fff';
    } else if (state === 'nav-ghost') {
      bg = 'transparent';
      border = '1.5px solid rgba(255, 255, 255, 0.16)';
      color = '#9aa3b2';
    } else {
      // idle
      bg = 'rgba(255, 255, 255, 0.04)';
      border = '1.5px solid rgba(255, 255, 255, 0.16)';
      color = '#f5f7fb';
    }

    btn.style.cssText = (btn.style.cssText || '') +
      ';background: ' + bg + ' !important' +
      ';background-color: ' + bg + ' !important' +
      ';background-image: none !important' +
      ';border: ' + border + ' !important' +
      ';color: ' + color + ' !important' +
      ';outline: none !important' +
      ';filter: none !important' +
      (extra ? (';' + extra) : ';box-shadow: none !important');
  }
  WS._forceButtonStyle = forceButtonStyle;

  /**
   * Defensive observer: if a theme strips inline styles after we set them,
   * re-apply them. Uses MutationObserver on style attribute changes.
   * Activated lazily on first button creation.
   */
  var _styleObserver = null;
  function ensureStyleObserver() {
    if (_styleObserver || typeof MutationObserver === 'undefined') return;
    _styleObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        if (m.type !== 'attributes') return;
        if (m.attributeName !== 'style') return;
        var t = m.target;
        if (!t || !t.hasAttribute || !t.hasAttribute('data-ws-state')) return;
        // If our forced styles were removed, re-apply
        var current = t.style.cssText || '';
        if (current.indexOf('!important') === -1) {
          forceButtonStyle(t, t.getAttribute('data-ws-state'));
        }
      });
    });
    _styleObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
      subtree: true
    });
  }
  // Init observer on first paint
  if (typeof document !== 'undefined' && document.body) {
    ensureStyleObserver();
  } else if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', ensureStyleObserver);
  }

  // Tooltip system
  var activeTip = null;

  function makeTooltip(text, linkText, linkUrl) {
    var tip = el('div', 'ws-tooltip');
    var p = el('div', '', { text: text });
    tip.appendChild(p);
    if (linkText && linkUrl) {
      var a = el('a', 'ws-tooltip-link', {
        href: linkUrl,
        target: '_blank',
        rel: 'noopener',
        text: linkText
      });
      tip.appendChild(a);
    }
    document.body.appendChild(tip);
    return tip;
  }

  function attachInfoIcon(parent, infoText, linkText, linkUrl) {
    if (!infoText) return;

    // Minimal "i" icon, accessible but not styled as a button
    var icon = el('span', 'ws-info-icon', {
      role: 'button',
      tabindex: '0',
      'aria-label': 'More info'
    });

    var tipEl = null;
    var hideTimer = null;

    function showTip() {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      if (tipEl) return;
      if (activeTip) { activeTip.remove(); activeTip = null; }
      tipEl = makeTooltip(infoText, linkText, linkUrl);
      activeTip = tipEl;
      var rect = icon.getBoundingClientRect();
      tipEl.style.left = (rect.left + rect.width / 2) + 'px';
      tipEl.style.top  = (rect.top - 8) + 'px';
      requestAnimationFrame(function() {
        tipEl.classList.add('ws-tooltip-show');
      });
    }
    function hideTipNow() {
      if (!tipEl) return;
      tipEl.remove();
      if (activeTip === tipEl) activeTip = null;
      tipEl = null;
    }
    function hideTipDelayed() {
      // Allow user time to move into the tooltip if they want to click the link
      hideTimer = setTimeout(hideTipNow, 200);
    }

    icon.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      if (tipEl) hideTipNow();
      else showTip();
    });
    icon.addEventListener('mouseenter', showTip);
    icon.addEventListener('mouseleave', hideTipDelayed);
    icon.addEventListener('focus', showTip);
    icon.addEventListener('blur', hideTipDelayed);

    document.addEventListener('click', function(e) {
      if (tipEl && e.target !== icon && !icon.contains(e.target)) {
        if (tipEl.contains(e.target)) return; // clicked inside tooltip, keep open
        hideTipNow();
      }
    });

    parent.appendChild(icon);
  }

  function wrapQuestion(config, content) {
    var wrap = el('div', 'ws-q', { 'data-q-id': config.id });

    // Thumbnail image (optional, prepended to question)
    if (config.thumbUrl) {
      var thumb = el('div', 'ws-q-thumb');
      // Optional: 'tall' aspect for full-body shots like handstand
      if (config.thumbAspect === 'tall') {
        thumb.classList.add('ws-q-thumb-tall');
      }
      // Force inline cssText on thumb wrapper to defeat theme img containers
      thumb.style.cssText = 'display:block !important;overflow:hidden !important;position:relative !important;flex-shrink:0 !important;';

      var img = el('img', '', {
        src: config.thumbUrl,
        alt: '',
        loading: 'lazy',
        decoding: 'async'
      });
      // Force inline cssText on the img itself — themes commonly apply
      // `width: auto !important` or `object-fit: contain !important` to all imgs.
      // We need cover behavior so the photo fills the entire thumb box.
      var objectPos = config.thumbPosition || 'center 40%';
      var IDLE_FILTER = 'grayscale(1) contrast(1.05) brightness(0.95)';
      var HOVER_FILTER = 'grayscale(0.2) contrast(1.05) brightness(1.05)';
      img.style.cssText =
        'display:block !important;' +
        'width:100% !important;' +
        'height:100% !important;' +
        'max-width:none !important;' +
        'max-height:none !important;' +
        'min-width:0 !important;' +
        'min-height:0 !important;' +
        'object-fit:cover !important;' +
        'object-position:' + objectPos + ' !important;' +
        'filter:' + IDLE_FILTER + ' !important;' +
        'transition:filter 0.3s ease !important;';
      thumb.appendChild(img);
      wrap.appendChild(thumb);

      // JS-driven hover (CSS :hover may not work if theme has overlay layers)
      // Apply filter directly via inline style with !important.
      wrap.addEventListener('mouseenter', function() {
        img.style.setProperty('filter', HOVER_FILTER, 'important');
      });
      wrap.addEventListener('mouseleave', function() {
        img.style.setProperty('filter', IDLE_FILTER, 'important');
      });
      wrap.addEventListener('focusin', function() {
        img.style.setProperty('filter', HOVER_FILTER, 'important');
      });
      wrap.addEventListener('focusout', function() {
        img.style.setProperty('filter', IDLE_FILTER, 'important');
      });
    }

    var head = el('div', 'ws-q-head');
    var labelRow = el('div', 'ws-q-label-row');

    var label = el('label', 'ws-q-label');
    label.textContent = t(config.labelKey);
    if (config.required) {
      var star = el('span', 'ws-q-required', { text: ' *' });
      label.appendChild(star);
    }
    if (config.optional) {
      var optBadge = el('span', 'ws-q-optional', { text: '(' + t('optional') + ')' });
      label.appendChild(optBadge);
    }
    labelRow.appendChild(label);

    if (config.infoKey) {
      var linkText = config.infoLinkKey ? t(config.infoLinkKey) : null;
      var rawLinkUrl  = config.infoLinkUrl || null;
      var linkUrl = (rawLinkUrl && WS.localizeUrl) ? WS.localizeUrl(rawLinkUrl) : rawLinkUrl;
      attachInfoIcon(labelRow, t(config.infoKey), linkText, linkUrl);
    }
    head.appendChild(labelRow);

    if (config.hintKey) {
      var hint = el('p', 'ws-q-hint');
      hint.textContent = t(config.hintKey);
      head.appendChild(hint);
    }
    wrap.appendChild(head);
    wrap.appendChild(content);

    var err = el('div', 'ws-q-error', { 'data-q-error': config.id });
    wrap.appendChild(err);

    // Warning slot (separate from error, for soft validation messages)
    var warn = el('div', 'ws-q-warning', { 'data-q-warning': config.id });
    warn.style.display = 'none';
    wrap.appendChild(warn);

    // Slot for contextual link (rendered when user selects qualifying option)
    if (config.contextLinks) {
      var ctxSlot = el('div', 'ws-context-slot', { 'data-q-ctx': config.id });
      wrap.appendChild(ctxSlot);
    }

    return wrap;
  }

  // Render contextual link in slot when value matches a trigger
  function updateContextLink(config, value) {
    var slot = document.querySelector('[data-q-ctx="' + config.id + '"]');
    if (!slot) return;
    slot.innerHTML = '';
    if (!config.contextLinks) return;

    var match = config.contextLinks.find(function(rule) {
      if (rule.values && rule.values.indexOf(String(value)) !== -1) return true;
      return false;
    });
    if (!match) return;

    var a = el('a', 'ws-context-link', {
      href: WS.localizeUrl ? WS.localizeUrl(match.url) : match.url,
      target: '_blank',
      rel: 'noopener'
    });
    var icon = el('span', 'ws-context-link-icon', { text: match.icon || '📖' });
    a.appendChild(icon);
    var txt = el('span', 'ws-context-link-text', { text: t(match.labelKey) });
    a.appendChild(txt);
    var arrow = el('span', 'ws-context-link-arrow', { text: '→' });
    a.appendChild(arrow);

    slot.appendChild(a);
  }

  // ============================================================
  // Number input
  // ============================================================
  WS.components.number_input = function(config) {
    var stored = WS.getAnswer(config.id);
    var current = stored != null ? stored : (config.defaultValue != null ? config.defaultValue : '');

    var wrap = el('div', 'ws-numinput-wrap');

    var input = el('input', 'ws-input ws-numinput', {
      type: 'number',
      inputmode: 'numeric'
    });
    if (config.min != null) input.setAttribute('min', String(config.min));
    if (config.max != null) input.setAttribute('max', String(config.max));
    input.setAttribute('step', String(config.step || 1));
    if (current !== '' && current != null) input.value = current;
    if (config.placeholderKey) input.placeholder = t(config.placeholderKey);

    input.addEventListener('input', function() {
      var v = input.value === '' ? '' : +input.value;
      WS.setAnswer(config.id, v);
      updateContextLink(config, v);
    });

    wrap.appendChild(input);

    if (config.unitKey) {
      var unit = el('span', 'ws-numinput-unit', { text: t(config.unitKey) });
      wrap.appendChild(unit);
    }

    var rendered = wrapQuestion(config, wrap);
    setTimeout(function() { updateContextLink(config, current); }, 0);
    return rendered;
  };

  // ============================================================
  // Number stepper
  // ============================================================
  WS.components.number_stepper = function(config) {
    var min = config.min != null ? config.min : 0;
    var max = config.max != null ? config.max : 99;
    var stp = config.step || 1;
    var def = config.defaultValue != null ? config.defaultValue : min;
    var current = WS.getAnswer(config.id, def);

    var stepper = el('div', 'ws-stepper');

    var minus = el('button', 'ws-stepper-btn', { type: 'button', 'aria-label': 'Decrease' });
    minus.innerHTML = '<span class="ws-stepper-icon" aria-hidden="true">−</span>';
    forceButtonStyle(minus, 'idle');

    var display = el('div', 'ws-stepper-display');
    var valEl = el('span', 'ws-stepper-value', { text: String(current) });
    display.appendChild(valEl);
    if (config.unitKey) {
      var unit = el('span', 'ws-stepper-unit', { text: t(config.unitKey) });
      display.appendChild(unit);
    }

    var plus = el('button', 'ws-stepper-btn', { type: 'button', 'aria-label': 'Increase' });
    plus.innerHTML = '<span class="ws-stepper-icon" aria-hidden="true">+</span>';
    forceButtonStyle(plus, 'idle');

    function setValue(newVal) {
      newVal = Math.max(min, Math.min(max, newVal));
      if (newVal === current) return;
      current = newVal;
      valEl.textContent = String(newVal);
      WS.setAnswer(config.id, newVal);
      updateContextLink(config, newVal);
      minus.disabled = newVal <= min;
      plus.disabled = newVal >= max;
      valEl.classList.remove('ws-pulse');
      void valEl.offsetWidth;
      valEl.classList.add('ws-pulse');
    }

    minus.addEventListener('click', function() { setValue(current - stp); });
    plus.addEventListener('click', function() { setValue(current + stp); });

    function bindHold(btn, delta) {
      var holdT, accelT;
      function start() {
        holdT = setTimeout(function() {
          var speed = 100;
          accelT = setInterval(function() {
            setValue(current + delta);
            if (speed > 30) speed -= 6;
          }, speed);
        }, 380);
      }
      function stop() { clearTimeout(holdT); clearInterval(accelT); }
      btn.addEventListener('pointerdown', start);
      btn.addEventListener('pointerup', stop);
      btn.addEventListener('pointercancel', stop);
      btn.addEventListener('pointerleave', stop);
    }
    bindHold(minus, -stp);
    bindHold(plus, stp);

    minus.disabled = current <= min;
    plus.disabled = current >= max;

    stepper.appendChild(minus);
    stepper.appendChild(display);
    stepper.appendChild(plus);

    var rendered = wrapQuestion(config, stepper);
    setTimeout(function() { updateContextLink(config, current); }, 0);
    return rendered;
  };

  WS.components.time_stepper = function(config) {
    var c = {};
    for (var k in config) c[k] = config[k];
    c.unitKey = c.unitKey || 'unit_seconds';
    return WS.components.number_stepper(c);
  };

  // ============================================================
  // Weighted input
  // ============================================================
  WS.components.weighted_input = function(config) {
    var stored = WS.getAnswer(config.id);
    var current = stored != null ? stored : '';

    var unitPref = WS.getAnswer('weighted_unit', WS._defaultWeightUnit || 'kg');
    if (unitPref !== 'kg' && unitPref !== 'lbs') unitPref = 'kg';

    function kgToLbs(kg) {
      if (kg === '' || kg == null) return '';
      return Math.round(kg * 2.20462);
    }
    function lbsToKg(lbs) {
      if (lbs === '' || lbs == null) return '';
      return Math.round(lbs / 2.20462);
    }

    var wrap = el('div', 'ws-numinput-wrap ws-weighted-input');

    var input = el('input', 'ws-input ws-numinput', {
      type: 'number',
      inputmode: 'numeric',
      min: '0',
      step: '1'
    });
    if (config.placeholderKey) input.placeholder = t(config.placeholderKey);

    function refreshDisplay() {
      if (current === '' || current == null) { input.value = ''; return; }
      input.value = unitPref === 'kg' ? current : kgToLbs(current);
    }
    refreshDisplay();

    input.addEventListener('input', function() {
      var v = input.value;
      if (v === '') {
        current = '';
        WS.setAnswer(config.id, '');
        return;
      }
      var num = +v;
      if (isNaN(num)) return;
      current = unitPref === 'kg' ? Math.round(num) : lbsToKg(num);
      WS.setAnswer(config.id, current);
    });

    wrap.appendChild(input);

    var unit = el('span', 'ws-numinput-unit ws-weighted-unit-label', {
      text: unitPref
    });
    wrap.appendChild(unit);

    document.addEventListener('ws:weight-unit-change', function(e) {
      unitPref = e.detail.unit;
      unit.textContent = unitPref;
      refreshDisplay();
    });

    return wrapQuestion(config, wrap);
  };

  // ============================================================
  // Weight unit toggle
  // ============================================================
  WS.components.weight_unit_toggle = function(config) {
    var current = WS.getAnswer('weighted_unit', WS._defaultWeightUnit || 'kg');
    if (current !== 'kg' && current !== 'lbs') current = 'kg';

    var wrap = el('div', 'ws-unit-toggle');
    var label = el('span', 'ws-unit-toggle-label', { text: t('weight_unit_label') });
    wrap.appendChild(label);

    var btnGroup = el('div', 'ws-unit-toggle-group', { role: 'radiogroup' });
    ['kg', 'lbs'].forEach(function(u) {
      var btn = el('button', 'ws-unit-toggle-btn', {
        type: 'button',
        role: 'radio',
        'data-unit': u,
        text: u
      });
      if (current === u) btn.classList.add('ws-unit-toggle-active');
      forceButtonStyle(btn, current === u ? 'active' : 'idle');
      btn.addEventListener('click', function() {
        if (current === u) return;
        current = u;
        WS.setAnswer('weighted_unit', u);
        btnGroup.querySelectorAll('.ws-unit-toggle-btn').forEach(function(b) {
          var isA = b.getAttribute('data-unit') === u;
          b.classList.toggle('ws-unit-toggle-active', isA);
          forceButtonStyle(b, isA ? 'active' : 'idle');
        });
        document.dispatchEvent(new CustomEvent('ws:weight-unit-change', { detail: { unit: u } }));
      });
      btnGroup.appendChild(btn);
    });
    wrap.appendChild(btnGroup);

    return wrap;
  };

  // ============================================================
  // Divider
  // ============================================================
  WS.components.divider = function(config) {
    var wrap = el('div', 'ws-divider');
    if (config.titleKey) {
      var title = el('div', 'ws-divider-title', { text: t(config.titleKey) });
      wrap.appendChild(title);
    }
    if (config.subtitleKey) {
      var sub = el('div', 'ws-divider-sub', { text: t(config.subtitleKey) });
      wrap.appendChild(sub);
    }
    return wrap;
  };

  // ============================================================
  // Step-level links block (subtle bottom links)
  // ============================================================
  WS.components.links_block = function(config) {
    var wrap = el('div', 'ws-step-links');

    if (config.labelKey) {
      var label = el('div', 'ws-step-links-label', { text: t(config.labelKey) });
      wrap.appendChild(label);
    }

    (config.links || []).forEach(function(link) {
      var a = el('a', '', {
        href: WS.localizeUrl ? WS.localizeUrl(link.url) : link.url,
        target: '_blank',
        rel: 'noopener',
        text: t(link.labelKey)
      });
      wrap.appendChild(a);
    });

    return wrap;
  };

  // ============================================================
  // Chips, single select
  // ============================================================
  WS.components.chips_single = function(config) {
    var options = config.options || [];
    var stored = WS.getAnswer(config.id);
    var current;
    if (stored != null) current = stored;
    else if (config.defaultValue != null) current = config.defaultValue;
    else current = undefined;

    var wrap = el('div', 'ws-chips-grid', { role: 'radiogroup' });
    if (config.cols) wrap.setAttribute('data-cols', String(config.cols));

    options.forEach(function(opt) {
      var chip = el('button', 'ws-chip-option', {
        type: 'button',
        role: 'radio',
        'data-value': opt.value
      });

      if (opt.icon) {
        var icon = el('span', 'ws-chip-icon');
        if (opt.icon.charAt(0) === '<') {
          icon.innerHTML = opt.icon; // SVG string
        } else {
          icon.textContent = opt.icon; // emoji or symbol
        }
        chip.appendChild(icon);
      }
      var lab = el('span', 'ws-chip-text', { text: t(opt.labelKey) });
      chip.appendChild(lab);

      function update() {
        var active = current != null && String(current) === String(opt.value);
        chip.classList.toggle('ws-chip-option-active', active);
        chip.setAttribute('aria-checked', active ? 'true' : 'false');
        forceButtonStyle(chip, active ? 'active' : 'idle');
      }

      chip.addEventListener('click', function() {
        current = opt.value;
        WS.setAnswer(config.id, current);
        wrap.querySelectorAll('.ws-chip-option').forEach(function(c) {
          var isA = c.getAttribute('data-value') === String(opt.value);
          c.classList.toggle('ws-chip-option-active', isA);
          c.setAttribute('aria-checked', isA ? 'true' : 'false');
          forceButtonStyle(c, isA ? 'active' : 'idle');
        });
        updateContextLink(config, current);
      });

      // Touch: fire on touchend if tap (not scroll), prevent 300ms ghost click
      var touchStartY = 0;
      chip.addEventListener('touchstart', function(e) {
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      chip.addEventListener('touchend', function(e) {
        var dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
        if (dy < 10) {
          e.preventDefault(); // kills the delayed ghost click
          current = opt.value;
          WS.setAnswer(config.id, current);
          wrap.querySelectorAll('.ws-chip-option').forEach(function(c) {
            var isA = c.getAttribute('data-value') === String(opt.value);
            c.classList.toggle('ws-chip-option-active', isA);
            c.setAttribute('aria-checked', isA ? 'true' : 'false');
            forceButtonStyle(c, isA ? 'active' : 'idle');
          });
          updateContextLink(config, current);
        }
      }, { passive: false });

      update();
      wrap.appendChild(chip);
    });

    var rendered = wrapQuestion(config, wrap);
    setTimeout(function() { updateContextLink(config, current); }, 0);
    return rendered;
  };

  // ============================================================
  // Chips, multi select
  // ============================================================
  WS.components.chips_multi = function(config) {
    var options = config.options || [];
    var def = config.defaultValue || [];
    var current = WS.getAnswer(config.id, def.slice());
    if (!Array.isArray(current)) current = [];

    var wrap = el('div', 'ws-chips-grid', { role: 'group' });

    options.forEach(function(opt) {
      var chip = el('button', 'ws-chip-option', {
        type: 'button',
        role: 'checkbox',
        'data-value': opt.value
      });

      if (opt.icon) {
        var icon = el('span', 'ws-chip-icon');
        if (opt.icon.charAt(0) === '<') {
          icon.innerHTML = opt.icon;
        } else {
          icon.textContent = opt.icon;
        }
        chip.appendChild(icon);
      }
      var lab = el('span', 'ws-chip-text', { text: t(opt.labelKey) });
      chip.appendChild(lab);
      function isSel() { return current.indexOf(opt.value) !== -1; }
      function update() {
        chip.classList.toggle('ws-chip-option-active', isSel());
        chip.setAttribute('aria-checked', isSel() ? 'true' : 'false');
        forceButtonStyle(chip, isSel() ? 'active' : 'idle');
      }

      chip.addEventListener('click', function() {
        var idx = current.indexOf(opt.value);
        if (idx === -1) current.push(opt.value);
        else current.splice(idx, 1);
        WS.setAnswer(config.id, current.slice());
        update();
      });

      // Instant touch response — don't wait for ghost click
      var touchStartY = 0;
      chip.addEventListener('touchstart', function(e) {
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      chip.addEventListener('touchend', function(e) {
        var dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
        if (dy < 10) {
          e.preventDefault();
          var idx = current.indexOf(opt.value);
          if (idx === -1) current.push(opt.value);
          else current.splice(idx, 1);
          WS.setAnswer(config.id, current.slice());
          update();
        }
      }, { passive: false });

      update();
      wrap.appendChild(chip);
    });

    return wrapQuestion(config, wrap);
  };

  // ============================================================
  // Tiers
  // ============================================================
  WS.components.tiers = function(config) {
    var options = config.options || [];
    var stored = WS.getAnswer(config.id);
    var current;
    if (stored != null) current = stored;
    else if (config.defaultValue != null) current = config.defaultValue;
    else current = undefined;

    var wrap = el('div', 'ws-tiers', { role: 'radiogroup' });

    options.forEach(function(opt, idx) {
      var card = el('button', 'ws-tier', {
        type: 'button',
        role: 'radio',
        'data-value': opt.value
      });

      var num = el('div', 'ws-tier-num', { text: String(idx) });
      card.appendChild(num);

      var body = el('div', 'ws-tier-body');
      var title = el('div', 'ws-tier-title', { text: t(opt.titleKey) });
      body.appendChild(title);
      if (opt.descKey) {
        var desc = el('div', 'ws-tier-desc', { text: t(opt.descKey) });
        body.appendChild(desc);
      }
      card.appendChild(body);

      var check = el('div', 'ws-tier-check');
      check.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>';
      card.appendChild(check);

      function update() {
        var active = current != null && String(current) === String(opt.value);
        card.classList.toggle('ws-tier-active', active);
        card.setAttribute('aria-checked', active ? 'true' : 'false');
        forceButtonStyle(card, active ? 'active' : 'idle');
      }

      card.addEventListener('click', function() {
        current = opt.value;
        WS.setAnswer(config.id, current);
        wrap.querySelectorAll('.ws-tier').forEach(function(c) {
          var isA = c.getAttribute('data-value') === String(opt.value);
          c.classList.toggle('ws-tier-active', isA);
          c.setAttribute('aria-checked', isA ? 'true' : 'false');
          forceButtonStyle(c, isA ? 'active' : 'idle');
        });
        updateContextLink(config, current);
      });

      update();
      wrap.appendChild(card);
    });

    var rendered = wrapQuestion(config, wrap);
    setTimeout(function() { updateContextLink(config, current); }, 0);
    return rendered;
  };

  // ============================================================
  // Yes/No
  // ============================================================
  WS.components.yes_no = function(config) {
    var stored = WS.getAnswer(config.id);
    var current;
    if (stored != null) current = stored;
    else if (config.defaultValue != null) current = config.defaultValue;
    else current = undefined;

    var wrap = el('div', 'ws-yesno', { role: 'radiogroup' });

    var yesBtn = el('button', 'ws-yesno-btn ws-yesno-yes', {
      type: 'button', role: 'radio', 'data-value': 'yes'
    });
    yesBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg><span>' + t('yes') + '</span>';

    var noBtn = el('button', 'ws-yesno-btn ws-yesno-no', {
      type: 'button', role: 'radio', 'data-value': 'no'
    });
    noBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg><span>' + t('no') + '</span>';

    function update() {
      yesBtn.classList.toggle('ws-yesno-active', current === 'yes');
      noBtn.classList.toggle('ws-yesno-active', current === 'no');
      yesBtn.setAttribute('aria-checked', current === 'yes' ? 'true' : 'false');
      noBtn.setAttribute('aria-checked', current === 'no' ? 'true' : 'false');
      forceButtonStyle(yesBtn, current === 'yes' ? 'yes-active' : 'idle');
      forceButtonStyle(noBtn,  current === 'no'  ? 'no-active'  : 'idle');
    }

    yesBtn.addEventListener('click', function() {
      current = 'yes';
      WS.setAnswer(config.id, current);
      update();
      updateContextLink(config, current);
    });
    noBtn.addEventListener('click', function() {
      current = 'no';
      WS.setAnswer(config.id, current);
      update();
      updateContextLink(config, current);
    });

    update();

    wrap.appendChild(yesBtn);
    wrap.appendChild(noBtn);

    var rendered = wrapQuestion(config, wrap);
    setTimeout(function() { updateContextLink(config, current); }, 0);
    return rendered;
  };

  // ============================================================
  // Text input
  // ============================================================
  WS.components.text = function(config) {
    var current = WS.getAnswer(config.id, config.defaultValue || '');

    var input = el('input', 'ws-input', {
      type: 'text',
      maxlength: String(config.maxLength || 60)
    });
    input.value = current;
    if (config.placeholderKey) input.placeholder = t(config.placeholderKey);

    input.addEventListener('input', function() {
      WS.setAnswer(config.id, input.value);
    });

    return wrapQuestion(config, input);
  };

  // ============================================================
  // Slider
  // ============================================================
  WS.components.slider = function(config) {
    var min = config.min || 0;
    var max = config.max || 100;
    var stp = config.step || 1;
    var def = config.defaultValue != null ? config.defaultValue : min;
    var current = WS.getAnswer(config.id, def);

    var wrap = el('div', 'ws-slider-wrap');

    var readout = el('div', 'ws-slider-readout');
    var valEl = el('span', 'ws-slider-value', { text: String(current) });
    readout.appendChild(valEl);
    if (config.unitKey) {
      var unit = el('span', 'ws-slider-unit', { text: t(config.unitKey) });
      readout.appendChild(unit);
    }

    var range = el('input', 'ws-slider', {
      type: 'range',
      min: String(min),
      max: String(max),
      step: String(stp)
    });
    range.value = String(current);

    range.addEventListener('input', function() {
      var v = +range.value;
      valEl.textContent = String(v);
      WS.setAnswer(config.id, v);
    });

    wrap.appendChild(readout);
    wrap.appendChild(range);

    return wrapQuestion(config, wrap);
  };

  // ============================================================
  // Nav buttons
  // ============================================================
  WS.components.navButtons = function(stepNum, totalSteps) {
    var wrap = el('div', 'ws-nav');

    if (stepNum > 1) {
      var back = el('button', 'ws-btn ws-btn-ghost', {
        type: 'button',
        text: t('back')
      });
      forceButtonStyle(back, 'nav-ghost');
      back.addEventListener('click', function() { WS.back(); });
      wrap.appendChild(back);
    }

    var spacer = el('div', 'ws-nav-spacer');
    wrap.appendChild(spacer);

    var lastStep = (totalSteps && stepNum === totalSteps - 1);
    var nextLabel = lastStep ? t('see_result') : t('next');
    var next = el('button', 'ws-btn ws-btn-primary', {
      type: 'button',
      text: nextLabel
    });
    forceButtonStyle(next, 'nav-primary');
    next.addEventListener('click', function() {
      if (typeof WS.tryNext === 'function') WS.tryNext();
      else WS.next();
    });
    wrap.appendChild(next);

    return wrap;
  };

})();